import React, { useMemo, useState } from 'react';
import { Loader2, Binoculars } from 'lucide-react';
import { getRelativeTime, getSpeciesEmoji } from '../hooks/useWildlife';
import { useTranslation } from 'react-i18next';

interface NatureScoutCardProps {
  lat: number;
  lon: number;
  species: any[];
  latestSeen: Date | null;
  loading: boolean;
  error: string | null;
}

const NatureScoutCard: React.FC<NatureScoutCardProps> = ({ lat, lon, species, latestSeen, loading, error }) => {
  const { t } = useTranslation();
  const [selectedSpecies, setSelectedSpecies] = useState<string | null>(null);
  const selectedItem = species.find((item) => item.species === selectedSpecies) || null;

  const tip = useMemo(() => {
    if (species.length === 0) return null;
    const day = new Date().getDate();
    const pivot = (selectedSpecies || species[0]?.species) as string;

    // Get tips from translation
    const tips = t(`natureScout.species.${pivot}.tips`, { returnObjects: true }) as string[];

    if (Array.isArray(tips) && tips.length > 0) {
      return tips[day % tips.length];
    }

    return t('natureScout.species_tip_default');
  }, [species, selectedSpecies, t]);

  return (
    <div className="bg-emerald-500/5 border border-emerald-500/20 p-4 rounded-2xl space-y-3">
      <div className="flex items-center gap-2">
        <Binoculars size={15} className="text-emerald-300" />
        <h4 className="text-tiny font-black uppercase tracking-widest text-emerald-200">📸 {t('natureScout.title')}</h4>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[var(--text-secondary)] text-xs">
          <Loader2 size={13} className="animate-spin" />
          {t('natureScout.loading')}
        </div>
      ) : error ? (
        <p className="text-xs text-[var(--text-secondary)]">{t('natureScout.error')}</p>
      ) : species.length === 0 ? (
        <p className="text-xs text-[var(--text-secondary)]">{t('natureScout.no_registrations')}</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {species.map((item) => (
              <button
                key={item.species}
                type="button"
                onClick={() => setSelectedSpecies((prev) => (prev === item.species ? null : item.species))}
                className={`text-left bg-[var(--bg-input)] border rounded-xl px-3 py-2 transition-colors ${selectedSpecies === item.species ? 'border-emerald-300/60 bg-emerald-500/10' : 'border-[var(--border-secondary)]'
                  }`}
              >
                <p className="text-tiny font-black text-[var(--text-primary)] truncate">
                  {getSpeciesEmoji(item.species)} {t(`natureScout.species.${item.species}.name`)} ({t('natureScout.observations_count', { count: item.count })})
                </p>
              </button>
            ))}
          </div>
          <p className="text-tiny text-[var(--text-secondary)]">{t('natureScout.tap_for_details')}</p>

          {selectedItem && (
            <div className="bg-black/35 border border-emerald-400/25 rounded-xl p-3 space-y-2">
              <p className="text-[12px] font-black text-[var(--text-primary)]">
                {getSpeciesEmoji(selectedItem.species)} {t(`natureScout.species.${selectedItem.species}.name`)}
              </p>
              <p className="text-tiny text-[var(--text-primary)] leading-relaxed">
                {t(`natureScout.species.${selectedItem.species}.detail`) || t('natureScout.species_detail_default')}
              </p>
              <p className="text-tiny text-[var(--text-secondary)]">
                {t('natureScout.last_seen_prefix')} {getRelativeTime(selectedItem.lastSeen || latestSeen, t)}.
              </p>
              {tip && <p className="text-tiny text-emerald-200 italic">{tip}</p>}
            </div>
          )}
        </>
      )}

      <p className="text-2xs text-[var(--text-muted)] leading-relaxed">
        {t('natureScout.disclaimer')}
      </p>
    </div>
  );
};

export default NatureScoutCard;
